import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FiCheck, FiUploadCloud, FiTrash2, FiImage } from "react-icons/fi";
import toast from "react-hot-toast";

/**
 * ProductSpecModal
 * 
 * Interactive specification modal matching the AddressModal animation & style:
 * - Backdrop: bg-black/30 backdrop-blur-sm transition-opacity duration-300
 * - Modal: w-full md:w-[520px] md:rounded-3xl rounded-t-3xl shadow-2xl transition-all duration-300
 * - Animation: scale-100 translate-y-0 vs scale-95 md:translate-y-4 translate-y-full
 * - Header: bg-primaryColor rounded-t-3xl with back arrow and title
 * - Shows Question & Buyer's Answer
 * - Radio selection: Yes / No / Not Sure
 * - Remarks / Thoughts text input for Yes/No
 * - Upload 1 Proof image with instant preview and remove functionality
 * - Clear and Confirm actions
 */
const ProductSpecModal = ({
  isOpen,
  onClose,
  selectedSpec,
  onSubmit,
  onClear,
}) => {
  const [animate, setAnimate] = useState(false);
  const [radioValue, setRadioValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [proofFiles, setProofFiles] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && selectedSpec) {
      setRadioValue(selectedSpec.radioValue || null);
      setInputValue(selectedSpec.inputValue || "");
      setProofFiles(selectedSpec.proofFiles || []);
      setTimeout(() => setAnimate(true), 10);
    } else {
      setAnimate(false);
    }
  }, [isOpen, selectedSpec]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  const handleRadioSelect = (val) => {
    setRadioValue(val);
    if (val === "Not Sure" || val === "Not_Sure") {
      setInputValue("");
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const file = files[0];
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file for proof.");
      return;
    }

    // Attach preview url for easy rendering
    const fileWithPreview = Object.assign(file, {
      preview: URL.createObjectURL(file),
    });

    setProofFiles([fileWithPreview]);
    e.target.value = "";
  };

  const handleRemoveProof = (e) => {
    e.stopPropagation();
    setProofFiles([]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!radioValue) {
      toast.error("Please select Yes, No, or Not Sure.");
      return;
    }

    onSubmit(radioValue, inputValue, proofFiles);
    handleClose();
  };

  const handleClear = () => {
    setProofFiles([]);
    setRadioValue(null);
    setInputValue("");
    onClear();
    handleClose();
  };

  if (!isOpen || !selectedSpec) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center
      bg-black/30 backdrop-blur-sm transition-opacity duration-300
      ${animate ? "opacity-100" : "opacity-0"}`}
      onClick={handleClose}
    >
      <div
        className={`bg-white w-full md:w-[520px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden
        ${animate ? "scale-100 translate-y-0" : "scale-95 md:translate-y-4 translate-y-full"}`}
        style={{ maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header - matches AddressModal style */}
        <div className="px-5 py-4 bg-primaryColor text-white flex items-center rounded-t-3xl md:rounded-t-3xl">
          <button
            type="button"
            onClick={handleClose}
            className="text-white text-2xl leading-none mr-2 hover:opacity-80 transition-opacity"
          >
            ←
          </button>
          <div className="flex-1 text-center font-semibold text-base sm:text-lg">
            Specification Verification
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-white/80 text-sm font-medium hover:text-white transition-opacity"
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-auto p-5 space-y-4">
          {/* Question & Buyer's Answer Card */}
          <div className="p-4 rounded-2xl bg-gray-50 border border-gray-200 space-y-2">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Question
              </p>
              <h4 className="text-xs sm:text-sm font-bold text-gray-900 mt-0.5">
                Q: {selectedSpec.title}
              </h4>
            </div>
            <div className="pt-2 border-t border-gray-200">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Buyer Answer
              </p>
              <p className="text-xs sm:text-sm font-extrabold text-primaryColor mt-0.5">
                A: {selectedSpec.value}
              </p>
            </div>
          </div>

          {/* Radio Options */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-700">
              Do you agree with this requirement? <span className="text-primaryColor">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {["Yes", "No", "Not Sure"].map((opt) => {
                const isSelected = radioValue === opt || (opt === "Not Sure" && radioValue === "Not_Sure");
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => handleRadioSelect(opt)}
                    className={`py-2.5 px-3 rounded-xl text-xs font-bold border transition flex items-center justify-center gap-2 ${
                      isSelected
                        ? "bg-primaryColor text-white border-primaryColor shadow-sm shadow-primaryColor/20"
                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <span
                      className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center ${
                        isSelected ? "border-white bg-white" : "border-gray-300"
                      }`}
                    >
                      {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-primaryColor" />}
                    </span>
                    <span>{opt}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Inputs for Yes / No */}
          {(radioValue === "Yes" || radioValue === "No") && (
            <div className="space-y-4 pt-1 animate-fadeIn">
              {/* Remarks Text Input */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700">
                  Supplier Remarks / Thoughts (Optional)
                </label>
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Enter your thoughts or specifics..."
                  className="w-full px-3.5 py-2.5 bg-white border border-gray-300 rounded-xl text-xs font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor"
                />
              </div>

              {/* Upload Proof */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-700 flex items-center justify-between">
                  <span>Upload Verification Proof (1 Image)</span>
                  {proofFiles.length > 0 && (
                    <span className="text-[10px] text-gray-400 font-normal">Click upload to replace</span>
                  )}
                </label>

                {/* Hidden File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />

                {/* Upload Trigger / Dropzone */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full py-3.5 px-4 border-2 border-dashed border-primaryColor/40 hover:border-primaryColor rounded-2xl bg-primaryColor/[0.02] hover:bg-primaryColor/[0.04] transition cursor-pointer flex flex-col items-center justify-center text-center gap-1.5"
                >
                  <FiUploadCloud className="text-2xl text-primaryColor" />
                  <p className="text-xs font-bold text-primaryColor">
                    {proofFiles.length > 0 ? "Replace Proof Image" : "Click to Upload Proof"}
                  </p>
                  <p className="text-[10px] text-gray-400">PNG, JPG, JPEG up to 10MB</p>
                </div>

                {/* Proof Preview Thumbnail */}
                {proofFiles.length > 0 && (
                  <div className="flex items-center gap-3 p-2.5 rounded-xl bg-gray-50 border border-gray-200">
                    <div className="relative w-14 h-14 rounded-lg overflow-hidden border border-gray-200 bg-white flex-shrink-0">
                      <img
                        src={
                          proofFiles[0].preview ||
                          (typeof proofFiles[0] === "string" ? proofFiles[0] : URL.createObjectURL(proofFiles[0]))
                        }
                        alt="Proof Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-gray-800 truncate">
                        {proofFiles[0].name || "Proof Document"}
                      </p>
                      <p className="text-[10px] text-emerald-600 font-bold mt-0.5">
                        ✓ Proof Attached
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveProof}
                      className="p-2 rounded-xl text-red-500 hover:bg-red-50 transition text-sm"
                      title="Remove proof"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </form>

        {/* Modal Footer - matches AddressModal style */}
        <div className="p-5 border-t flex gap-3 bg-white">
          <button
            type="button"
            onClick={handleClear}
            className="flex-1 py-3.5 border-2 border-gray-300 text-gray-700 rounded-2xl font-medium hover:bg-gray-50 transition-colors text-sm"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-1 py-3.5 bg-primaryColor text-white rounded-2xl font-semibold hover:bg-primaryColor/90 transition-colors text-sm flex items-center justify-center gap-2"
          >
            <FiCheck className="text-base" /> Confirm & Submit
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProductSpecModal;
